<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import api from 'dashboard/api/sourceRecovery';
import Button from 'dashboard/components-next/button/Button.vue';

const user = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');
const allowed = computed(() =>
  user.value?.accounts?.some(a => String(a.id) === String(accountId.value) && a.role === 'administrator') ||
  (user.value?.ui_settings?.chat_operator_account_ids || []).map(String).includes(String(accountId.value))
);
const sources = ref([]);
const selected = ref(null);
const qr = ref(null);
const error = ref('');
const password = ref('');
const loading = ref(false);
const checkError = ref(false);
const noticed = new Set();
const dialog = ref(null);
let previousFocus;
function close() {
  selected.value = null;
  password.value = '';
  qr.value = null;
}
watch(selected, async (value, oldValue) => {
  if (value && !oldValue) {
    previousFocus = document.activeElement;
    await nextTick();
    dialog.value?.querySelector('button')?.focus();
  } else if (!value) {
    previousFocus?.focus?.();
  }
});
function trapFocus(event) {
  if (event.key !== 'Tab') return;
  const items = [...dialog.value.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
  const first = items[0];
  const last = items.at(-1);
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault(); last?.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault(); first?.focus();
  }
}
let timer;
let stopped = false;
let checking = false;
const disconnected = computed(() => sources.value.filter(s => s.status !== 'connected'));
const qrSrc = computed(() => {
  const value = qr.value?.qr_png_base64;
  return value ? (value.startsWith('data:') ? value : `data:image/png;base64,${value}`) : '';
});
const instructions = computed(() => selected.value?.provider === 'whatsapp_personal'
  ? 'WhatsApp → Настройки (или ⋮) → Связанные устройства → Привязать устройство.'
  : 'Telegram → Настройки → Устройства → Подключить устройство.');

async function refreshQr() {
  if (!selected.value || loading.value) return;
  const id = selected.value.id;
  loading.value = true;
  error.value = '';
  try {
    const { data } = await api.reconnect(id);
    if (stopped || selected.value?.id !== id) return;
    qr.value = data;
    if (data.status === 'connected' || data.status === 'authorized') {
      selected.value = null;
      noticed.delete(id);
      sources.value = sources.value.filter(s => s.id !== id);
    }
  } catch (e) {
    if (stopped || selected.value?.id !== id) return;
    error.value = e.response?.data?.error || 'Не удалось получить QR. Попробуйте ещё раз.';
  } finally {
    loading.value = false;
  }
}

async function open(source) {
  if (loading.value) return;
  selected.value = source;
  noticed.add(source.id);
  password.value = '';
  qr.value = null;
  await refreshQr();
}

async function check() {
  if (!allowed.value || checking) return;
  checking = true;
  try {
    const { data } = await api.status();
    if (stopped) return;
    checkError.value = false;
    sources.value = data;
    data.filter(s => s.status === 'connected').forEach(s => noticed.delete(s.id));
    if (selected.value && data.some(s => s.id === selected.value.id && s.status === 'connected')) selected.value = null;
    const lost = disconnected.value.find(s => !noticed.has(s.id));
    if (lost && !selected.value) await open(lost);
    else if (selected.value) await refreshQr();
  } catch (e) {
    checkError.value = true;
  } finally {
    checking = false;
  }
}

async function submitPassword() {
  if (!password.value || !selected.value || loading.value) return;
  loading.value = true;
  try {
    const { data } = await api.password(selected.value.id, password.value);
    qr.value = data;
    if (data.status === 'connected') selected.value = null;
  } catch (e) {
    error.value = e.response?.data?.error || 'Не удалось подтвердить пароль.';
  } finally {
    password.value = '';
    loading.value = false;
  }
}

onMounted(() => { check(); timer = window.setInterval(check, 30000); });
onUnmounted(() => { stopped = true; window.clearInterval(timer); password.value = ''; qr.value = null; });
</script>

<template>
  <div v-if="allowed && (disconnected.length || checkError)" class="p-3 border-b border-n-weak bg-n-amber-2 text-sm text-n-slate-12">
    <p v-if="checkError" class="m-0">Проверка источников временно недоступна.</p>
    <div v-for="source in disconnected" :key="source.id" class="flex items-center justify-between gap-2">
      <span>{{ source.name }} — нет связи</span>
      <button class="text-n-blue-11 underline" type="button" @click="open(source)">Подключить</button>
    </div>
  </div>
  <Teleport to="body">
    <div v-if="selected" class="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" @click.self="close">
      <section ref="dialog" role="dialog" aria-modal="true" aria-labelledby="source-recovery-title" class="w-full max-w-md max-h-[90dvh] overflow-y-auto rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-xl" @keydown.esc="close" @keydown="trapFocus">
        <div class="flex justify-between items-start gap-3">
          <h2 id="source-recovery-title" class="text-lg font-semibold text-n-slate-12">Подключить {{ selected.name }}</h2>
          <button type="button" aria-label="Закрыть подключение" class="p-2 text-n-slate-11" @click="close">✕</button>
        </div>
        <p class="text-sm text-n-slate-11">Откройте на телефоне: {{ instructions }} Затем отсканируйте QR с экрана компьютера. Дождитесь статуса подключения.</p>
        <p v-if="error" role="alert" class="text-sm text-n-amber-11">{{ error }}</p>
        <div v-if="qrSrc" class="mx-auto w-64 max-w-full rounded-lg bg-white p-3">
          <img :src="qrSrc" alt="QR для подключения источника" class="w-full" />
        </div>
        <p v-else class="text-sm text-n-slate-11">{{ loading ? 'Готовим QR…' : 'QR пока не готов. Обновите его через несколько секунд.' }}</p>
        <form v-if="qr?.status === 'waiting_password'" class="mt-3 flex flex-col gap-2" @submit.prevent="submitPassword">
          <label class="text-sm text-n-slate-12" for="source-recovery-password">Пароль двухэтапной проверки Telegram</label>
          <input id="source-recovery-password" v-model="password" type="password" autocomplete="off" class="form-input" />
          <Button label="Подтвердить пароль" :disabled="loading" type="submit" />
        </form>
        <Button type="button" class="mt-4" label="Обновить QR / проверить подключение" :disabled="loading" @click="refreshQr" />
      </section>
    </div>
  </Teleport>
</template>
